import React, { useState, useCallback, useTransition } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IoClose } from "react-icons/io5";
import { toast } from 'react-toastify';
import { useChatbotConfig } from '../../contexts/ChatbotConfigContext';
import azureReminderService from '../../services/azureReminderService';

const ReminderForm = React.memo(({ showForm, setShowForm, setMessages }) => {
    const { config } = useChatbotConfig();
    const [submitted, setSubmitted] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        date: "",
        time: ""
    });
    const [formErrors, setFormErrors] = useState({
        name: "",
        date: "",
        time: ""
    });
    const [isPending, startTransition] = useTransition();

    const formVariants = {
        hidden: { opacity: 0, height: 0, transition: { duration: 0.3, ease: "easeInOut" } },
        visible: { opacity: 1, height: "auto", transition: { duration: 0.3, ease: "easeInOut" } },
    };

    const inputVariants = {
        hidden: { opacity: 0, y: -10 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeInOut" } },
    };

    const validateField = useCallback((name, value) => {
        let error = "";
        const currentDate = new Date();
        
        switch (name) {
            case "name":
                if (!value.trim()) {
                    error = "Client name is required.";
                } else if (value.trim().length < 2) {
                    error = "Client name must be at least 2 characters.";
                }
                break;
            case "date":
                if (!value) {
                    error = "Date is required.";
                } else {
                    const selectedDate = new Date(value);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    
                    if (selectedDate < today) {
                        error = "Please select today or a future date.";
                    }
                }
                break;
            case "time":
                if (!value) {
                    error = "Time is required.";
                } else if (formData.date) {
                    const selectedDateTime = new Date(`${formData.date}T${value}`);
                    const now = new Date();
                    // Add 5 minutes buffer for Azure reminder system
                    const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000);
                    
                    if (selectedDateTime <= fiveMinutesFromNow) {
                        error = "Please select a time at least 5 minutes in the future.";
                    }
                }
                break;
            default:
                break;
        }
        return error;
    }, [formData.date]);

    const handleFormChange = useCallback((e) => {
        const { name, value } = e.target;
        startTransition(() => {
            setFormData(prevData => ({
                ...prevData,
                [name]: value
            }));
            setFormErrors(prevErrors => ({
                ...prevErrors,
                [name]: validateField(name, value)
            }));
        });
    }, [validateField]);

    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        
        // Validate all fields
        const errors = {
            name: validateField("name", formData.name),
            date: validateField("date", formData.date),
            time: validateField("time", formData.time)
        };
        setFormErrors(errors);

        // Check if there are any errors
        if (Object.values(errors).some(error => error !== "")) {
            toast.error("Please correct the errors in the form.");
            return;
        }

        setSubmitted(true);

        try {
            // Call Azure service to create reminder
            console.log('🔔 Submitting reminder to Azure:', formData);
            console.log('📱 Using device token:', azureReminderService.deviceToken);
            
            const result = await azureReminderService.createReminder(formData);
            
            console.log('✅ Azure response:', result);
            
            if (result.success) {
                // Show success toast
                // toast.success("Reminder scheduled successfully! 🎉");
                
                // Format the success message
                const meetingDateTime = new Date(`${formData.date}T${formData.time}`);
                const formattedDate = meetingDateTime.toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                });
                const formattedTime = meetingDateTime.toLocaleTimeString('en-IN', { 
                    hour: '2-digit', 
                    minute: '2-digit',
                    hour12: true
                });
                
                const reminderCount = result.data?.meetingDetails?.reminderCount || 0;
                const reminderDetails = result.data?.meetingDetails?.reminders || [];
                
                // Create detailed success message
                let successMessage = `Perfect! I've scheduled a meeting with ${formData.name} on ${formattedDate} at ${formattedTime}.`;
                
                if (reminderCount > 0) {
                    successMessage += `\n\nYou'll receive ${reminderCount} notification${reminderCount > 1 ? 's' : ''}:`;
                    reminderDetails.forEach(reminder => {
                        successMessage += `\n• ${reminder.type} (${reminder.timeIST})`;
                    });
                } else {
                    successMessage += "\n\nThe meeting is too soon for advance reminders.";
                }
                
                // Save to local storage for reminder chat history
                const reminderForStorage = {
                    reminderId: result.data.reminderId,
                    clientName: formData.name,
                    meetingDate: meetingDateTime.toISOString(),
                    createdAt: new Date().toISOString(),
                    status: 'active',
                    schedulingMessage: successMessage,
                    notificationMessages: [],
                    source: 'azure',
                    azureData: result.data,
                    reminderCount: reminderCount,
                    deviceToken: azureReminderService.deviceToken // Store the device token used
                };
                
                // Update localStorage for reminder chat history
                const existingReminders = localStorage.getItem("chatbot_reminders");
                let reminders = [];
                if (existingReminders) {
                    try {
                        reminders = JSON.parse(existingReminders);
                        // Ensure it's an array
                        if (!Array.isArray(reminders)) {
                            reminders = [];
                        }
                    } catch (error) {
                        console.error("Error parsing existing reminders:", error);
                        reminders = [];
                    }
                }
                reminders.push(reminderForStorage);
                localStorage.setItem("chatbot_reminders", JSON.stringify(reminders));
                
                // Add message to chat
                setMessages(prev => [
                    ...prev, 
                    { 
                        type: "bot",
                        content: successMessage,
                        timestamp: new Date(),
                        isReminderConfirmation: true,
                        reminderData: reminderForStorage
                    }
                ]);
                
                // Reset form
                setFormData({ name: "", date: "", time: "" });
                setFormErrors({ name: "", date: "", time: "" });
                
                // Close form after delay
                setTimeout(() => {
                    setShowForm(false);
                    setSubmitted(false);
                }, 2000);
                
            } else {
                throw new Error(result.error || 'Failed to create reminder');
            }
            
        } catch (error) {
            console.error("❌ Error creating reminder:", error);
            toast.error(error.message || "Failed to set reminder. Please try again.");
            setSubmitted(false);
        }
    }, [formData, validateField, setMessages, setShowForm]);

    const cancelForm = useCallback(() => {
        setShowForm(false);
        setFormData({ name: "", date: "", time: "" });
        setFormErrors({ name: "", date: "", time: "" });
        setSubmitted(false);
    }, [setShowForm]);

    // Set default date to today and time to next hour
    React.useEffect(() => {
        const now = new Date();
        const nextHour = new Date(now.getTime() + 60 * 60 * 1000);
        
        if (!formData.date) {
            setFormData(prev => ({
                ...prev,
                date: now.toISOString().split('T')[0]
            }));
        }
        
        if (!formData.time) {
            const timeString = nextHour.toTimeString().slice(0, 5);
            setFormData(prev => ({
                ...prev,
                time: timeString
            }));
        }
    }, [formData.date, formData.time]);

    return (
        <AnimatePresence>
            {showForm && (
                <motion.form
                    variants={formVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    onSubmit={handleFormSubmit}
                    className="mb-2 p-2 bg-gray-100 rounded-lg relative"
                >
                    <motion.button
                        type="button"
                        onClick={cancelForm}
                        className="absolute -top-8 right-2 bg-gray-200 rounded-full"
                        style={{ 
                            color: config.color
                        }}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                    >
                        <IoClose size={20} />
                    </motion.button>

                    <motion.div variants={inputVariants}>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleFormChange}
                            placeholder="Name of client"
                            className={`w-full mb-2 p-2 rounded bg-white text-gray-800 ${formErrors.name ? 'border border-red-500' : ''}`}
                            required
                        />
                        {formErrors.name && <p className="text-red-500 text-xs mt-1 mb-2">{formErrors.name}</p>}
                    </motion.div>

                    <motion.div variants={inputVariants}>
                        <input
                            type="date"
                            name="date"
                            value={formData.date}
                            onChange={handleFormChange}
                            className={`w-full mb-2 p-2 rounded bg-white text-gray-800 ${formErrors.date ? 'border border-red-500' : ''}`}
                            required
                        />
                        {formErrors.date && <p className="text-red-500 text-xs mt-1 mb-2">{formErrors.date}</p>}
                    </motion.div>

                    <motion.div variants={inputVariants}>
                        <input
                            type="time"
                            name="time"
                            value={formData.time}
                            onChange={handleFormChange}
                            className={`w-full mb-2 p-2 rounded bg-white text-gray-800 ${formErrors.time ? 'border border-red-500' : ''}`}
                            required
                        />
                        {formErrors.time && <p className="text-red-500 text-xs mt-1 mb-2">{formErrors.time}</p>}
                    </motion.div>

                    <motion.button
                        type="submit"
                        style={{ backgroundColor: submitted ? '#10B981' : config.color }}
                        className={`w-full p-2 text-white rounded hover:bg-opacity-90 ${
                            submitted ? 'cursor-not-allowed' : ''
                        }`}
                        whileHover={!submitted ? { scale: 1.02 } : {}}
                        whileTap={!submitted ? { scale: 0.98 } : {}}
                        disabled={submitted || isPending}
                    >
                        {submitted ? (
                            <span className="flex items-center justify-center">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Reminder Set ✅
                            </span>
                        ) : (
                            "Set Reminder"
                        )}
                    </motion.button>
                </motion.form>
            )}
        </AnimatePresence>
    );
});

export default ReminderForm;